import '../App.css';
import {
    Box,
    Card, CardContent,
    createTheme, CssBaseline, ThemeProvider,
    Stack, Typography, useMediaQuery,
    FormControl, Select, MenuItem,
    IconButton, Chip,
} from "@mui/material";
import {ArrowBack} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
    AreaChart, Area, BarChart, Bar, LineChart, Line, Cell, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from "recharts";
import SideBar from "../Componant/sidebar/Sidebar";
import {getQrCode, getQrCodeAnalytics, getQrCodeActivity, getQrCodeCitiesForCountry} from "../Utils/qrcodesApi";
import {CATEGORICAL_COLORS, OTHER_COLOR, SINGLE_SERIES_COLOR} from "../Componant/QrCode/chartColors";
import WorldMap from "../Componant/QrCode/WorldMap";

const RANGE_OPTIONS = [
    {value: '7', label: '7 derniers jours'},
    {value: '30', label: '30 derniers jours'},
    {value: '90', label: '90 derniers jours'},
    {value: 'all', label: 'Depuis le début'},
];

const ACTIVITY_PERIODS = [
    {value: 'today', label: "Aujourd'hui"},
    {value: '7d', label: '7 derniers jours'},
    {value: 'month', label: 'Mois'},
    {value: 'year', label: 'Année'},
];

function StatTile({label, value}) {
    return (
        <Card variant="outlined" sx={{flex: 1, minWidth: 160}}>
            <CardContent>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="h4">{value}</Typography>
            </CardContent>
        </Card>
    );
}

function BreakdownChart({title, data}) {
    const chartData = useMemo(() => data.map((d, i) => ({
        ...d,
        fill: i < CATEGORICAL_COLORS.length ? CATEGORICAL_COLORS[i] : OTHER_COLOR,
    })), [data]);

    return (
        <Card variant="outlined" sx={{flex: '1 1 380px', minWidth: 320}}>
            <CardContent>
                <Typography variant="h6" gutterBottom>{title}</Typography>
                {chartData.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Pas encore de données</Typography>
                ) : (
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={chartData} layout="vertical" margin={{left: 24}}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false}/>
                            <XAxis type="number" allowDecimals={false}/>
                            <YAxis type="category" dataKey="label" width={100}/>
                            <RechartsTooltip/>
                            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                                {chartData.map((entry, i) => <Cell key={i} fill={entry.fill}/>)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
}

function HourOfDayChart({data = []}) {
    const chartData = useMemo(() => data.map((d) => ({label: `${d.hour}h`, count: d.count})), [data]);
    const hasData = chartData.some((d) => d.count > 0);

    return (
        <Card variant="outlined" sx={{mb: 3}}>
            <CardContent>
                <Typography variant="h6" gutterBottom>Heures de la journée</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Répartition des scans par heure (UTC), toutes dates confondues sur la période sélectionnée.
                </Typography>
                {!hasData ? (
                    <Typography variant="body2" color="text.secondary">Pas encore de données</Typography>
                ) : (
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="label" interval={1}/>
                            <YAxis allowDecimals={false}/>
                            <RechartsTooltip/>
                            <Bar dataKey="count" fill={SINGLE_SERIES_COLOR} radius={[4, 4, 0, 0]}/>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
}

function formatActivityLabel(dateStr, period) {
    const date = new Date(dateStr);
    if (period === 'today') return date.toLocaleTimeString('fr-FR', {hour: '2-digit'});
    if (period === 'year') return date.toLocaleDateString('fr-FR', {month: 'short', year: '2-digit'});
    if (period === '7d') return date.toLocaleDateString('fr-FR', {weekday: 'short', day: 'numeric'});
    return date.toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'});
}

const ACTIVITY_SERIES = [
    {key: 'total', name: 'Total', color: CATEGORICAL_COLORS[0]},
    {key: 'newVisitors', name: 'Nouveaux visiteurs', color: CATEGORICAL_COLORS[2]},
    {key: 'returningVisitors', name: 'Visiteurs récurrents', color: CATEGORICAL_COLORS[1]},
];

function ActivityChart({qrcodeId}) {
    const [period, setPeriod] = useState('7d');
    const [activity, setActivity] = useState([]);
    const [hiddenSeries, setHiddenSeries] = useState([]);

    useEffect(() => {
        getQrCodeActivity(qrcodeId, period).then(setActivity).catch((e) => console.error(e));
    }, [qrcodeId, period]);

    const chartData = useMemo(() => activity.map((point) => ({
        label: formatActivityLabel(point.date, period),
        total: point.total,
        newVisitors: point.newVisitors,
        returningVisitors: point.returningVisitors,
    })), [activity, period]);

    const toggleSeries = ({dataKey}) => {
        setHiddenSeries((prev) => prev.includes(dataKey) ? prev.filter((key) => key !== dataKey) : [...prev, dataKey]);
    };

    return (
        <Card variant="outlined" sx={{mb: 3}}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">Activité</Typography>
                    <FormControl size="small">
                        <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
                            {ACTIVITY_PERIODS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
                {chartData.every((p) => p.total === 0) ? (
                    <Typography variant="body2" color="text.secondary">Pas encore de données</Typography>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="label"/>
                            <YAxis allowDecimals={false}/>
                            <RechartsTooltip/>
                            <Legend onClick={toggleSeries} cursor="pointer"/>
                            {ACTIVITY_SERIES.map((series) => (
                                <Line
                                    key={series.key}
                                    type="monotone"
                                    dataKey={series.key}
                                    name={series.name}
                                    stroke={series.color}
                                    strokeWidth={2}
                                    dot={false}
                                    hide={hiddenSeries.includes(series.key)}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
}

function QrCodeAnalyticsPage() {
    const {id} = useParams();
    const navigate = useNavigate();
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const isComputer = useMediaQuery('(min-width:900px)');
    const [sidebarIsOpen, setSidebarIsOpen] = useState();

    const [qrcode, setQrcode] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [range, setRange] = useState('30');
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [countryCities, setCountryCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(false);

    const darkTheme = createTheme({
        palette: {
            mode: prefersDarkMode ? 'dark' : 'light',
            background: {default: prefersDarkMode ? '#1A1A1A' : '#f5f5f5'},
            primary: {main: '#FF7233', light: '#FF7233', dark: '#e05a1a'},
        },
        typography: {
            fontFamily: '"Poppins", sans-serif',
            allVariants: {color: prefersDarkMode ? '#cecece' : '#000000FF'},
        },
    });

    const rangeParams = useMemo(() => {
        if (range === 'all') return {};
        const from = new Date();
        from.setDate(from.getDate() - parseInt(range, 10));
        return {from: from.toISOString()};
    }, [range]);

    useEffect(() => {
        getQrCode(id).then(setQrcode).catch((e) => console.error(e));
    }, [id]);

    useEffect(() => {
        getQrCodeAnalytics(id, rangeParams).then(setAnalytics).catch((e) => console.error(e));
        setSelectedCountry(null);
        setCountryCities([]);
    }, [id, rangeParams]);

    const handleCountryClick = useCallback((code, name) => {
        setSelectedCountry({code, name});
        setCitiesLoading(true);
        getQrCodeCitiesForCountry(id, code, rangeParams)
            .then(setCountryCities)
            .catch((e) => console.error(e))
            .finally(() => setCitiesLoading(false));
    }, [id, rangeParams]);

    const timeseriesData = useMemo(() => {
        if (!analytics) return [];
        return analytics.timeseries.map((point) => ({
            date: new Date(point.date).toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'}),
            count: point.count,
        }));
    }, [analytics]);

    const busiestDay = useMemo(() => {
        if (!analytics || analytics.timeseries.length === 0) return '—';
        const top = analytics.timeseries.reduce((a, b) => (b.count > a.count ? b : a));
        return new Date(top.date).toLocaleDateString('fr-FR', {day: '2-digit', month: 'long'});
    }, [analytics]);

    const getContainerClass = () => {
        const classes = [];
        if (!isComputer) {
            classes.push("mobile");
        } else {
            classes.push(sidebarIsOpen ? 'activated' : 'disactivated');
        }
        return classes.join(' ');
    };

    return (
        <div className="App">
            <ThemeProvider theme={darkTheme}>
                <SideBar sidebarIsOpen={sidebarIsOpen} setSidebarIsOpen={setSidebarIsOpen} isComputer={isComputer}/>
                <CssBaseline/>

                <Box paddingTop={'5%'} paddingBottom={'5%'} id='Md-Container' className={getContainerClass()}>
                    <Box sx={{width: '100%', px: 2}}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                            <IconButton onClick={() => navigate('/my-qrcodes')}><ArrowBack/></IconButton>
                            <Box flex={1}>
                                <Typography variant="h4">{qrcode?.name || '...'}</Typography>
                                <Typography variant="body2" color="text.secondary">{qrcode?.targetUrl}</Typography>
                            </Box>
                            {qrcode && !qrcode.isActive && <Chip label="En pause" color="warning"/>}
                            <FormControl size="small">
                                <Select value={range} onChange={(e) => setRange(e.target.value)}>
                                    {RANGE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        {analytics && (
                            <>
                                <Stack direction="row" spacing={2} flexWrap="wrap" mb={3}>
                                    <StatTile label="Total scans" value={analytics.summary.totalScans}/>
                                    <StatTile label="Scans aujourd'hui" value={analytics.summary.scansToday}/>
                                    <StatTile label="Nouveaux visiteurs (approx.)" value={analytics.summary.newVisitors}/>
                                    <StatTile label="Visiteurs récurrents (approx.)" value={analytics.summary.returningVisitors}/>
                                    <StatTile label="Jour le plus actif" value={busiestDay}/>
                                    {qrcode && (qrcode.expiresAt || qrcode.maxScans != null) && (
                                        <StatTile label="Scans bloqués (expiré/limite)" value={analytics.summary.blockedScans}/>
                                    )}
                                </Stack>

                                <Card variant="outlined" sx={{mb: 3}}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>Scans dans le temps</Typography>
                                        {timeseriesData.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">Pas encore de données</Typography>
                                        ) : (
                                            <ResponsiveContainer width="100%" height={340}>
                                                <AreaChart data={timeseriesData}>
                                                    <CartesianGrid strokeDasharray="3 3"/>
                                                    <XAxis dataKey="date"/>
                                                    <YAxis allowDecimals={false}/>
                                                    <RechartsTooltip/>
                                                    <Area type="monotone" dataKey="count" name="Scans"
                                                          stroke={SINGLE_SERIES_COLOR} fill={SINGLE_SERIES_COLOR}
                                                          fillOpacity={0.25} strokeWidth={2}/>
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        )}
                                    </CardContent>
                                </Card>

                                <Stack direction="row" spacing={2} flexWrap="wrap" mb={3}>
                                    <BreakdownChart title="Appareils" data={analytics.devices}/>
                                    <BreakdownChart title="Navigateurs" data={analytics.browsers}/>
                                    <BreakdownChart title="Systèmes d'exploitation" data={analytics.os}/>
                                </Stack>

                                <Card variant="outlined" sx={{mb: 3}}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>Répartition géographique</Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Cliquez sur un pays pour voir ses villes les plus scannées.
                                        </Typography>
                                        <Stack direction={{xs: 'column', md: 'row'}} spacing={3} alignItems="flex-start">
                                            <Box sx={{flex: 1, minWidth: 0}}>
                                                <WorldMap
                                                    data={analytics.countries}
                                                    selectedCountry={selectedCountry?.code}
                                                    onCountryClick={handleCountryClick}
                                                />
                                            </Box>
                                            <Box sx={{width: {xs: '100%', md: 280}}}>
                                                {!selectedCountry ? (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Sélectionnez un pays sur la carte.
                                                    </Typography>
                                                ) : (
                                                    <>
                                                        <Typography variant="subtitle1" gutterBottom>{selectedCountry.name}</Typography>
                                                        {citiesLoading ? (
                                                            <Typography variant="body2" color="text.secondary">Chargement...</Typography>
                                                        ) : countryCities.length === 0 ? (
                                                            <Typography variant="body2" color="text.secondary">Aucune ville identifiée</Typography>
                                                        ) : (
                                                            <Stack spacing={1}>
                                                                {countryCities.map((city) => (
                                                                    <Stack key={city.label} direction="row" justifyContent="space-between">
                                                                        <Typography variant="body2">{city.label}</Typography>
                                                                        <Typography variant="body2" color="text.secondary">{city.count}</Typography>
                                                                    </Stack>
                                                                ))}
                                                            </Stack>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>

                                <Stack direction="row" spacing={2} flexWrap="wrap" mb={3}>
                                    <BreakdownChart title="Langues" data={analytics.languages}/>
                                </Stack>

                                <HourOfDayChart data={analytics.hourOfDay}/>

                                <ActivityChart qrcodeId={id}/>
                            </>
                        )}
                    </Box>
                </Box>
            </ThemeProvider>
        </div>
    );
}

export default QrCodeAnalyticsPage;
